import { createParamContext } from 'app/components/router/createParamContext';
import { EntityStringIdRouteCodec } from 'app/constants/Routes/codecs';
import {
  BookingQueryParam,
  BookingTypeCodec,
  IBookingTuple,
} from 'app/models/rtc/booking/constants';
import * as io from 'io-ts';
import { pipe } from 'fp-ts/lib/pipeable';
import { fold } from 'fp-ts/lib/Either';

/**
 * Booking query is either full with id, type and optional host key
 * or just host key.
 *
 * Id and type are always validated together
 */
type BookingParams = IBookingTuple | Pick<IBookingTuple, 'hostKey'>;

export const HostKeyCodec = io.type({
  [BookingQueryParam.BookingHostKey]: io.string,
});

export const BookingQueryCodec = io.type({
  [BookingQueryParam.BookingId]: EntityStringIdRouteCodec,
  [BookingQueryParam.BookingType]: BookingTypeCodec,
});

export const {
  Provider: BookingQueryProvider,
  useParams: useBookingQuery,
} = createParamContext<BookingParams>({
  serialize: (value) => {
    const bookingParams = (
      'bookingId' in value
      ? BookingQueryCodec.encode({
        [BookingQueryParam.BookingId]: value.bookingId,
        [BookingQueryParam.BookingType]: value.bookingType,
      })
      : {}
    );

    const hostKeyParams = (
      'hostKey' in value && value.hostKey
      ? HostKeyCodec.encode({
        [BookingQueryParam.BookingHostKey]: value.hostKey,
      })
      : {}
    );

    return { ...bookingParams, ...hostKeyParams };
  },
  deserialize: (query): BookingParams => {
    const bookingParams = pipe(
      BookingQueryCodec.decode(query),
      fold(
        () => ({}),
        (params) => ({
          bookingId:   params[BookingQueryParam.BookingId],
          bookingType: params[BookingQueryParam.BookingType],
        }),
      ),
    );

    const hostKeyPart = pipe(
      HostKeyCodec.decode(query),
      fold(
        () => ({}),
        (params) => ({
          hostKey: params[BookingQueryParam.BookingHostKey],
        }),
      ),
    );

    return { ...bookingParams, ...hostKeyPart };
  },
});
